import React , { useRef, useState, useEffect } from 'react'
import { urlFor } from '../lib/client'
import {Camera} from "react-camera-pro";
import VisDiv from './frontend_components/VisDiv';
import {AiFillCamera, AiFillCheckCircle} from 'react-icons/ai'
import { MdDelete, MdOutlineCheck, MdCameraFront, MdCameraRear, MdArrowForward, MdArrowBack } from 'react-icons/md';
import ImageCrop from './imageCrop/ImageCrop';
import useMediaQuery from '../hooks/useMediaQuery';

const ModalContent = ({setOpenModal, initialIndex, beforePicture, takePicture, savePictureBase64=()=>{}, setModalContent, title, body, continueTo, image, fullImage, children, continueDisabled, picturesForSlider }) => {
  
    const closeModal = () =>{
        setOpenModal(false)
        setModalContent(undefined) 
    }
    const camera = useRef(undefined);
 
    const [cameraLoaded, setCameraLoaded] = useState(false)
    const [nCameras, setNCameras] = useState(0)
    const [cameraError, setCameraError] = useState(undefined)
    const [facingMode, setFacingMode] = useState(() => {
        // Try to get the last used camera mode from localStorage
        if (typeof window !== 'undefined') {
            return localStorage.getItem('preferredCamera') || 'user'
        }
        return 'user'
    })

    // Key to force Camera component remount
    const [cameraKey, setCameraKey] = useState(0)

    // Track active stream for cleanup
    const activeStreamRef = useRef(undefined);

    useEffect(() => {
        return () => {
            // Cleanup camera stream on unmount
            if (activeStreamRef.current) {
                const tracks = activeStreamRef.current.getTracks();
                tracks.forEach(track => track.stop());
                activeStreamRef.current = undefined;
            }
        };
    }, []);

    const handleCameraStart = async (num) => {
        try {
            console.log('Camera started', num);
            
            // Get active video stream
            if (camera.current?.video) {
                const stream = camera.current.video.srcObject;
                if (stream) {
                    // Store stream for cleanup
                    activeStreamRef.current = stream;
                    
                    // Get video track
                    const videoTrack = stream.getVideoTracks()[0];
                    if (videoTrack) {
                        // Apply constraints for better iOS compatibility
                        await videoTrack.applyConstraints({
                            width: { ideal: 1920 },
                            height: { ideal: 1920 },
                            facingMode: facingMode
                        });

                        // Get and log settings
                        const settings = videoTrack.getSettings();
                        console.log('Camera settings:', settings);
                        
                        // Verify camera is streaming
                        if (!videoTrack.enabled || videoTrack.muted) {
                            throw new Error('Camera track is disabled or muted');
                        }

                        // Add track ended listener
                        videoTrack.addEventListener('ended', () => {
                            console.log('Video track ended');
                            setCameraError('Camera stream ended unexpectedly');
                            setCameraLoaded(false);
                        });
                    }
                }
            }

            setCameraError(undefined);
            setCameraLoaded(true);
            setNCameras(num);
        } catch (error) {
            console.error('Camera initialization error:', error);
            setCameraError(error.message);
            setCameraLoaded(false);
        }
    };

    const [photo, setPhoto] = useState(undefined);
    const [cropping, setCropping] = useState(false)

    const screenWidth = screen.width
    const windowWidth = window.innerWidth

    const cameraWidthRatio = beforePicture ? 2.5 : 2.2
    const cameraMaxWidth = (window.innerHeight - 130) / cameraWidthRatio

    const continueDisabledColor = continueDisabled && "grey"

    const takeSnapshot = async () => {
        try {
            if (!camera?.current) {
                throw new Error('Camera not initialized');
            }

            // Ensure camera is ready
            if (!cameraLoaded) {
                throw new Error('Camera not fully loaded');
            }

            // Take photo with max quality
            const snap = await camera.current.takePhoto();
            if (!snap) {
                throw new Error('Failed to capture photo');
            }

            console.log('Photo captured successfully');
            setPhoto(snap);
        } catch (error) {
            console.error('Error taking photo:', error);
            setCameraError(error.message);
        }
    }
    const deleteSnapshot = () =>{
        setPhoto(undefined)
        setCropping(false)
        setCameraLoaded(false)
    }
    const croppingFn = (data) => {
        setCropping(data)
    }
    const saveCroppedPhoto = (data) => {
        setPhoto(data)
    }
    const testFunction = () => {
        console.log("PHOTO STATE", photo)
        navigator.clipboard.writeText(photo)
    }
    const saveSnapshot = () => {
        savePictureBase64(photo)
        closeModal()
    }

    const toggleCamera = async () => {
        // Stop current stream before switching
        if (activeStreamRef.current) {
            const tracks = activeStreamRef.current.getTracks();
            tracks.forEach(track => track.stop());
            activeStreamRef.current = undefined;
        }

        setCameraLoaded(false); // Reset loaded state when switching cameras
        const newMode = facingMode === 'user' ? 'environment' : 'user';
        setFacingMode(newMode);
        
        // Force Camera component remount
        setCameraKey(prev => prev + 1);
        
        // Save the preferred camera mode
        if (typeof window !== 'undefined') {
            localStorage.setItem('preferredCamera', newMode);
        }

        // Small delay to ensure previous stream is fully stopped
        await new Promise(resolve => setTimeout(resolve, 100));
    }
    const canvasSize = cropping ? { height: "100%" } : {height:"120px"}
    if(takePicture) return (
        <ModalDiv fullScreen={true} closeModal={closeModal}>
            <div style={{display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center"}}>
                <VisDiv rules={[beforePicture]}>
                    <img 
                        src={beforePicture?.asset ? urlFor(beforePicture)?.width(2020)?.height(2020)?.url() : beforePicture}
                        style={{width: "100%", maxWidth: cameraMaxWidth}}
                        className="product-image"
                    />
                </VisDiv>
                <VisDiv rules={[!photo, !cropping]} style={{ width: "100%", maxWidth: cameraMaxWidth}}>
                    {cameraError ? (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: 'red'
                        }}>
                            Camera Error: {cameraError}
                            <button 
                                onClick={() => {
                                    setCameraError(undefined);
                                    setCameraKey(prev => prev + 1);
                                }}
                                style={{
                                    marginTop: '10px',
                                    padding: '5px 10px'
                                }}
                            >
                                Retry Camera
                            </button>
                        </div>
                    ) : (
                        <Camera 
                            key={cameraKey}
                            ref={camera}
                            aspectRatio={1/1}
                            facingMode={facingMode}
                            numberOfCamerasCallback={(num) => {
                                if (num > 0) {
                                    handleCameraStart(num);
                                }
                            }}
                            errorMessages={{
                                noCameraAccessible: 'No camera device accessible. Please connect your camera or try a different browser.',
                                permissionDenied: 'Permission denied. Please refresh and give camera permission.',
                                switchCamera:
                                  'It is not possible to switch camera to different one because there is only one video device accessible.',
                                canvas: 'Canvas is not supported.',
                            }}
                        />
                    )}
                </VisDiv>
                <VisDiv rules={[photo]} style={{ width: "100%", maxWidth: cameraMaxWidth}}>
                    <img 
                        src={photo}
                        style={
                            {
                            width: "100%", 
                            maxWidth: cameraMaxWidth
                        }}
                        className="product-image"
                    />
                </VisDiv>
                <VisDiv style={{...canvasSize}}>        
                    <VisDiv rules={[!photo]} style={{display:"flex", justifyContent:"center", marginTop:"10px", maxWidth: cameraMaxWidth,}}>
                        <VisDiv style={{marginRight:"15px"}} rules={[!cropping, nCameras > 1, cameraLoaded]}>
                            <button onClick={toggleCamera} style={{width:"100px", height:"100px", marginRight:"15px"}} className='round-red-button'>
                                {facingMode === 'user' ? 
                                    <MdCameraRear size={45} color="white" /> :
                                    <MdCameraFront size={45} color="white" />
                                }
                            </button>
                        </VisDiv>

                        <VisDiv style={{marginRight:"15px"}} rules={[!cropping, cameraLoaded]}>
                            <button onClick={takeSnapshot} style={{width:"100px", height:"100px"}} className='round-red-button'>
                                <AiFillCamera
                                size={45}
                                color={"white"}
                                />
                            </button>
                        </VisDiv>

                        <VisDiv style={{marginLeft:"15px", zIndex:"100"}}>
                            <ImageCrop customWidth={cameraMaxWidth} iconSize={100} isCropping={croppingFn} hidePreview={true} saveFn={saveCroppedPhoto}/> 
                        </VisDiv>
                    
                    </VisDiv>
                    <VisDiv rules={[photo]} style={{display:"flex", width:"100%", justifyContent:"space-between", marginTop:"10px", maxWidth: cameraMaxWidth}}>
                        <button onClick={saveSnapshot} style={{ backgroundColor:"green", width:"100px", height:"100px", marginRight:"15px"}} className='round-red-button'>
                            <MdOutlineCheck
                            size={45}
                            color={"white"}
                            />
                        </button>
                        <button onClick={deleteSnapshot} style={{width:"100px", height:"100px"}} className='round-red-button'>
                            <MdDelete
                            size={45}
                            color={"white"}
                            />
                        </button>
                    </VisDiv>
                </VisDiv>
            </div>
        </ModalDiv>
    )
    
    if (fullImage) return (
        <ModalDiv fullScreen={true} closeModal={closeModal}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"center"}}>
                <img 
                    src={urlFor(fullImage && fullImage).width(2020).height(2020).url()}
                    style={{width: "100%", height:"100%", maxHeight:"88vh", maxWidth:"88vh"}}
                    className="product-image"
                />
            </div>
        </ModalDiv>
    )
    if (picturesForSlider) return (
        <ModalDiv fullScreen={true} innerStyle={{width:"98%"}} closeModal={closeModal}>
            <Slider initialIndex={initialIndex} images = {picturesForSlider}/>
        </ModalDiv>
    )
    return (
        <ModalDiv closeModal={closeModal}>
                { title && 
                    <div className="title">
                        <h1>{title}</h1>
                    </div>
                }
                { image && 
                    <div className="image">
                        <h1>IMAGE TO IMPLEMENT</h1>
                    </div>
                }
                {children}
                { body && 
                    <div className="body">
                        {body}
                    </div> 
                }
                { continueTo && 
                    <div className="footer">
                        <button
                            onClick={() => {
                            closeModal();
                            }}
                            id="cancelBtn"
                        >
                            Cancel
                        </button>
                
                        <button style={{backgroundColor: continueDisabledColor}} disabled={continueDisabled} onClick={()=>{
                            continueTo()
                            closeModal()
                        }}>Continue</button>
                    </div>
                }
    </ModalDiv> 
  )
}

export default ModalContent

export const ModalDiv = ({children, innerStyle={}, style={}, fullScreen, closeModal = ()=>{}} ) => {
    const modalWidth =  () => {
        
        if(fullScreen) return {width: "95%", height: "95%"} 
        return {}}
    return (
        <div onClick={() => {closeModal()}} className="modalBackground" style={{ ...style}}>
            <div 
                onClick={(e) => e.stopPropagation()} 
                style={{
                    ...modalWidth(), 
                    ...innerStyle,
                    touchAction: "manipulation",
                    WebkitOverflowScrolling: "touch"
                }} 
                className="modalContainer"
            >
            <div className="titleCloseBtn">
                    <button
                    onClick={() => {
                        closeModal();
                    }}
                    >
                    X
                    </button>
                </div>
                <div>
                    {children}
                </div>
           
            </div>
        </div>
    )
}

export const Slider = ({images, initialIndex = 0}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [touchStart, setTouchStart] = useState(undefined);
    const [touchEnd, setTouchEnd] = useState(undefined);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);

    const minSwipeDistance = 50;

    const handleTouchStart = (e) => {
        setTouchStart(e.touches[0].clientX);
        setIsDragging(true);
        setDragOffset(0);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        setTouchEnd(e.touches[0].clientX);
        const offset = e.touches[0].clientX - touchStart;
        setDragOffset(offset);
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            setCurrentIndex(prev => (prev + 1) % images.length);
        }
        if (isRightSwipe) {
            setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
        }

        setTouchStart(undefined);
        setTouchEnd(undefined);
        setDragOffset(0);
    };

    const screenSizeDesktop = useMediaQuery("(min-width: 800px)");

    const handlePrevClick = () => {
        setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
    };

    const handleNextClick = () => {
        setCurrentIndex(prev => (prev + 1) % images.length);
    };

    return (
        <div 
            style={{
                height: "87vh",
                position: "relative",
                overflow: "hidden",
                touchAction: "manipulation"
            }}
            onTouchStart={(e) => {
                // Only handle single finger touches for sliding
                if (e.touches.length === 1) {
                    handleTouchStart(e);
                }
            }}
            onTouchMove={(e) => {
                // Only handle single finger touches for sliding
                if (e.touches.length === 1) {
                    handleTouchMove(e);
                }
            }}
            onTouchEnd={handleTouchEnd}
        >
            {screenSizeDesktop && (
                <>
                    <button
                        onClick={handlePrevClick}
                        style={{
                            position: 'absolute',
                            left: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            background: 'rgba(0, 0, 0, 0.5)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <MdArrowBack size={24} color="white" />
                    </button>
                    <button
                        onClick={handleNextClick}
                        style={{
                            position: 'absolute',
                            right: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            background: 'rgba(0, 0, 0, 0.5)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <MdArrowForward size={24} color="white" />
                    </button>
                </>
            )}
            <div style={{
                display: "flex",
                height: "100%",
                transform: `translateX(${-currentIndex * 100 + (dragOffset / window.innerWidth) * 100}%)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease-out'
            }}>
                {!Array.isArray(images) ? [] : images?.map?.((image, index) => (
                    <div 
                        key={index} 
                        style={{
                            flex: "0 0 100%",
                            width: "100%"
                        }}
                    >
                        <SliderPhoto image={image}/>
                    </div>
                ))}
            </div>
        </div>
    );
}

export const SliderPhoto = ({image})=> {
        const screenSizeDesktop = useMediaQuery("(min-width: 800px)")
        const onlineBeforePicture = image?.before?.sanityImage
        const offlineBeforePicture = image?.before?.base64Image
        const beforePicture = onlineBeforePicture ? onlineBeforePicture : offlineBeforePicture
        const beforePictureUrl = onlineBeforePicture ? urlFor(onlineBeforePicture).width(2020).height(2020)?.url() : beforePicture

        const onlineAfterPicture = image?.after?.sanityImage
        const offlineAfterPicture = image?.after?.base64Image
        const afterPicture = onlineAfterPicture ? onlineAfterPicture : offlineAfterPicture

        const afterPictureUrl = onlineAfterPicture?.asset ? urlFor(afterPicture).width(2020).height(2020)?.url() : afterPicture
        
    return(
        <div style={{display:"flex", justifyContent:"center", alignItems:"center",  width:"100%", height:"100%" }}>
            <VisDiv rules={[!afterPicture]} style={{display:"flex", alignItems:"center", justifyContent:"center"}}>
                <img 
                    src={beforePictureUrl}
                    style={{
                        width: "90%", 
                        height:"90%", 
                        maxHeight:"66vh", 
                        maxWidth:"66vh", 
                        touchAction: "manipulation"
                    }}
                    className="product-image"
                />
            </VisDiv>
            <VisDiv rules={[afterPicture, screenSizeDesktop]} style={{display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"row"}}>
                <VisDiv  style={{flex:1, display:"flex", justifyContent:"center", alignItems:"center"}}>
                    <img 
                        src={beforePictureUrl}
                        style={{
                            height:"90%",
                            width:"90%", 
                            aspectRatio:"1/1", 
                            touchAction: "manipulation"
                        }}
                        className="product-image"
                    />

                </VisDiv>
                <VisDiv style={{flex:1, display:"flex", justifyContent:"center", alignItems:"center"}}>
                    <img 
                        src={afterPictureUrl}
                        style={{
                        height:"90%",
                        width:"90%", 
                        aspectRatio:"1/1", 
                        touchAction: "manipulation"
                    }}
                        className="product-image"
                    />
                </VisDiv>
            </VisDiv>
            <VisDiv rules={[afterPicture, !screenSizeDesktop]} style={{display:"flex",  flexDirection:"column", }}>
                <VisDiv style={{marginBottom:"15px", flex:1, display:"flex", justifyContent:"center", alignItems:"center"}}>
                    <img 
                        src={beforePictureUrl}
                        style={{
                            width: "95%", 
                            height:"95%", 
                            maxHeight:"43vh", 
                            maxWidth:"43vh", 
                            aspectRatio:"1/1", 
                            touchAction: "manipulation"
                        }}
                        className="product-image"
                    />

                </VisDiv>
                <VisDiv style={{marginTop:"15px", flex:1, display:"flex", justifyContent:"center", alignItems:"center"}}>
                    <img 
                        src={afterPictureUrl}
                        style={{
                            width: "95%", 
                            height:"95%", 
                            maxHeight:"43vh", 
                            maxWidth:"43vh", 
                            aspectRatio:"1/1", 
                            touchAction: "manipulation"
                        }}
                        className="product-image"
                    />
                </VisDiv>

            </VisDiv>
        </div>
    )
}
